import { startOfDay, subDays } from "date-fns";
import { createParser, useQueryState } from "nuqs";
import { useTransition } from "react";
import { DateRange } from "react-day-picker";

// Custom parser that preserves timezone information
const parseAsLocalDate = createParser({
  parse: (value: string) => {
    const date = startOfDay(new Date(value));

    return isNaN(date.getTime()) ? null : date;
  },
  serialize: (value: Date) => {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  },
});

export type NonNullableFields<T> = {
  [P in keyof T]: NonNullable<T[P]>;
};

export type DateRangeSafe = NonNullableFields<Required<DateRange>>;

export const useDateRangeFilter = () => {
  const [from, setFrom] = useQueryState(
    "from",
    parseAsLocalDate.withDefault(subDays(startOfDay(new Date()), 6)),
  );

  const [to, setTo] = useQueryState(
    "to",
    parseAsLocalDate.withDefault(startOfDay(new Date())),
  );

  const [isPending, startTransition] = useTransition();

  const setRange = (newRange: DateRangeSafe) => {
    startTransition(() => {
      setFrom(newRange.from);
      setTo(newRange.to);
    });
  };

  return {
    range: { from, to } satisfies DateRangeSafe,
    setRange,
    isPending,
  };
};

export const isDateRangeSafe = (
  range: DateRange | undefined,
): range is DateRangeSafe => {
  if (!range) {
    return false;
  }

  if (!range.from || !range.to) {
    return false;
  }

  return true;
};
